import type { ReactNode } from 'react';

import { useAuth } from '@/lib/auth/auth-provider';
import { Button } from '@/components/ui/button';

/**
 * Minimal chrome for the 5-step onboarding wizard.
 *
 * Deliberately no left nav / top bar — the user hasn't earned a
 * dashboard yet. Just the wordmark, a sign-out escape hatch, and a
 * centered content column. The same pattern Stripe uses for its
 * activation flow.
 *
 * Children render their own card/heading/form. The shell only owns
 * the page background, the header bar, and the max-width container.
 *
 * `wide` lets a step opt into a roomier max-width when the content
 * is a multi-column grid (Step 4's carrier picker, eventually the
 * settings pages). Defaults to the narrow form-friendly width.
 */
export function OnboardingShell({
  children,
  wide = false,
}: {
  readonly children: ReactNode;
  readonly wide?: boolean;
}) {
  const { signOut } = useAuth();

  return (
    <div className="bg-muted/30 flex min-h-screen flex-col">
      <header className="border-border bg-background border-b">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-6 py-4">
          <span className="text-foreground text-lg font-semibold tracking-tight">Mailchute.ai</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              signOut();
            }}
          >
            Sign out
          </Button>
        </div>
      </header>

      <main
        className={
          'mx-auto flex w-full flex-1 flex-col px-6 py-12 ' + (wide ? 'max-w-5xl' : 'max-w-2xl')
        }
      >
        {children}
      </main>
    </div>
  );
}
